import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface CaptchaState {
  isVerified: boolean;
  token: string | null;
  isProcessing: boolean;
  error: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class CaptchaService {
  private captchaStateSubject = new BehaviorSubject<CaptchaState>({
    isVerified: false,
    token: null,
    isProcessing: false,
    error: null
  });

  public captchaState$: Observable<CaptchaState> = this.captchaStateSubject.asObservable();

  // URLs that require captcha verification
  private protectedEndpoints = [
    '/calculadora/regime-geral',
    //'/calculadora/pedagio',
    //'/split-payment'
  ];

  constructor() {}

  get currentState(): CaptchaState {
    return this.captchaStateSubject.value;
  }

  get isVerified(): boolean {
    return this.currentState.isVerified;
  }

  get token(): string | null {
    return this.currentState.token;
  }

  get exigeCaptcha(): boolean {
    return String(environment.exigeCaptcha) === 'true';
  }

  get siteKey(): string {
    return environment.captchaSiteKey;
  }

  /**
   * Check if a URL requires captcha verification
   */
  requiresCaptcha(url: string): boolean {
    return this.protectedEndpoints.some(endpoint => url.includes(endpoint));
  }

  /**
   * Set captcha as verified with token
   */
  setVerified(token: string): void {
    this.captchaStateSubject.next({
      isVerified: true,
      token,
      isProcessing: false,
      error: null
    });
  }

  /**
   * Set captcha as expired
   */
  setExpired(): void {
    this.captchaStateSubject.next({
      isVerified: false,
      token: null,
      isProcessing: false,
      error: null
    });
  }

  /**
   * Set captcha processing state
   */
  setProcessing(isProcessing: boolean): void {
    this.captchaStateSubject.next({
      ...this.currentState,
      isProcessing,
      error: null
    });
  }

  /**
   * Set captcha error
   */
  setError(error: string): void {
    this.captchaStateSubject.next({
      isVerified: false,
      token: null,
      isProcessing: false,
      error
    });
  }

  /**
   * Clear captcha state
   */
  clear(): void {
    this.captchaStateSubject.next({
      isVerified: false,
      token: null,
      isProcessing: false,
      error: null
    });
  }

  /**
   * Reset captcha state (for new verification)
   */
  reset(): void {
    this.clear();
  }
}
